import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppThunkAPIType } from "../../../../app/appTypes"
import { getWithAuth, postWithAuth, putWithAuth } from "../../../../http"

export type DeviationId = number

export type DeviationStatus = "NEW" | "EVALUATED" | "ANALYZED" | "CORRECTED" | "CLOSED"

export type DeviationClassification = 'MINOR' | 'MAJOR' | 'CRITICAL'

export interface Deviation {
    id: DeviationId
    documentId: number
    order: number
    status: DeviationStatus
    expectedResult: string
    actualResult: string
    hasImpactOnProductQuality: boolean | null
    classification: DeviationClassification
    isQualificationProcessContinuationAllowed: boolean | null
    evaluationJustification: string | null
    rootCause: string | null
    proposedCorrectiveActions: string | null
    correctiveActionsDeadline: string | null
    performedCorrectiveActions: string | null
    retestDescription: string | null
    retestTestId: number | null
    retestTestName: string | null
    testProtocolReference: string | null
    acceptanceCriteriaMet: boolean | null
    correctiveActionsRemarks: string | null
    isDeviationCorrected: boolean | null
    canDeviationBeClosed: boolean | null
    closingRemarks: string | null
    reporterUserName: string | null
    reporterFullName: string | null
    reporterPosition: string | null
    reporterCompany: string | null
    reporterSignatureDate: string | null
    decisionMakerUserName: string | null
    decisionMakerFullName: string | null
    decisionMakerPosition: string | null
    decisionMakerCompany: string | null
    decisionMakerSignatureDate: string | null
    analystUserName: string | null
    analystFullName: string | null
    analystPosition: string | null
    analystCompany: string | null
    analystSignatureDate: string | null
    correctorUserName: string | null
    correctorFullName: string | null
    correctorPosition: string | null
    correctorCompany: string | null
    correctorSignatureDate: string | null
    closerUserName: string | null
    closerFullName: string | null
    closerPosition: string | null
    closerCompany: string | null
    closerSignatureDate: string | null
    tests: TestDescriptor[]
}

export interface TestDescriptor {
    documentTestId:number 
    testName: string | null
}

export const defDeviation: Deviation = {
    id: 0,
    documentId: 0,
    order: 0,
    status: 'NEW',
    expectedResult: '',
    actualResult: '',
    hasImpactOnProductQuality: false,
    classification: 'MINOR',
    isQualificationProcessContinuationAllowed: false,
    evaluationJustification: '',
    rootCause: '',
    proposedCorrectiveActions: '',
    correctiveActionsDeadline: '',
    performedCorrectiveActions: '',
    retestDescription: '',
    retestTestId: 0,
    retestTestName: '',
    testProtocolReference: '',
    acceptanceCriteriaMet: false,
    correctiveActionsRemarks: '',
    isDeviationCorrected: false,
    canDeviationBeClosed: false,
    closingRemarks: '',
    reporterUserName: '',
    reporterFullName: '',
    reporterPosition: '',
    reporterCompany: '',
    reporterSignatureDate: null,
    decisionMakerUserName: '',
    decisionMakerFullName: '',
    decisionMakerPosition: '',
    decisionMakerCompany: '',
    decisionMakerSignatureDate: null,
    analystUserName: '',
    analystFullName: '',
    analystPosition: '',
    analystCompany: '',
    analystSignatureDate: null,
    correctorUserName: '',
    correctorFullName: '',
    correctorPosition: '',
    correctorCompany: '',
    correctorSignatureDate: null,
    closerUserName: '',
    closerFullName: '',
    closerPosition: '',
    closerCompany: '',
    closerSignatureDate: null,
    tests: [],

}

export const loadDeviations = createAsyncThunk<Deviation[], { documentId: string }, AppThunkAPIType>(
    "document/load-deviations",
    async ({ documentId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/document-deviations/${documentId}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as Deviation[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const createDeviation = createAsyncThunk<Deviation, Deviation, AppThunkAPIType>(
    "document/create-deviation",
    async (data, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/document-deviations/${data.documentId}`,
            payload: {...data, testIds: data.tests.map(t => t.documentTestId)},
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as Deviation
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateDeviation = createAsyncThunk<Deviation, Deviation, AppThunkAPIType>(
    "document/update-deviation",
    async (data, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/document-deviations/${data.documentId}/${data.id}`,
            payload: {...data, testIds: data.tests.map(t => t.documentTestId)},
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as Deviation
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface EvaluateDeviationArg {
    hasImpactOnProductQuality: boolean
    classification: DeviationClassification
    isQualificationProcessContinuationAllowed: boolean
    evaluationJustification: string
    decisionMakerUserName: string | null
    decisionMakerFullName: string | null
    decisionMakerPosition: string | null
    decisionMakerCompany: string | null
    decisionMakerSignatureDate: string | null
    id: number
    documentId: number
}
export const evaluateDeviation = createAsyncThunk<Deviation, EvaluateDeviationArg, AppThunkAPIType>(
    "document/evaluate-deviation",
    async (arg, { dispatch, rejectWithValue }) => {
        const { id, documentId, ...data } = arg
        const result = await dispatch(putWithAuth({
            url: `api/document-deviations/${documentId}/${id}/evaluation`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as Deviation
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface AnalyseDeviationArg {
    rootCause: string
    proposedCorrectiveActions: string
    correctiveActionsDeadline: string
    analystUserName: string | null
    analystFullName: string | null
    analystPosition: string | null
    analystCompany: string | null
    analystSignatureDate: string | null
    id: number
    documentId: number
}
export const analyseDeviation = createAsyncThunk<Deviation, AnalyseDeviationArg, AppThunkAPIType>(
    "document/analyse-deviation",
    async (arg, { dispatch, rejectWithValue }) => {
        const { id, documentId, ...data } = arg
        const result = await dispatch(putWithAuth({
            url: `api/document-deviations/${documentId}/${id}/analysis`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as Deviation
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface CorrectionDeviationArg {
    performedCorrectiveActions: string
    retestDescription: string
    testProtocolReference: string
    acceptanceCriteriaMet: boolean
    correctiveActionsRemarks: string
    correctorUserName: string | null
    correctorFullName: string | null
    correctorPosition: string | null
    correctorCompany: string | null
    correctorSignatureDate: string | null
    id: number
    documentId: number
}
export const correctionDeviation = createAsyncThunk<Deviation, CorrectionDeviationArg, AppThunkAPIType>(
    "document/correction-deviation",
    async (arg, { dispatch, rejectWithValue }) => {
        const { id, documentId, ...data } = arg
        const result = await dispatch(putWithAuth({
            url: `api/document-deviations/${documentId}/${id}/corrective-actions`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as Deviation
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface CloseDeviationArg {
    isDeviationCorrected: boolean
    canDeviationBeClosed: boolean
    closingRemarks: string
    closerUserName: string | null
    closerFullName: string | null
    closerPosition: string | null
    closerCompany: string | null
    id: number
    documentId: number
}
export const closeDeviation = createAsyncThunk<Deviation, CloseDeviationArg, AppThunkAPIType>(
    "document/close-deviation",
    async (arg, { dispatch, rejectWithValue }) => {
        const { id, documentId, ...data } = arg
        const result = await dispatch(putWithAuth({
            url: `api/document-deviations/${documentId}/${id}/closure`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as Deviation
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)
